// components/funding/MeetingScheduleDialog.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Chip,
  Stack,
  IconButton,
  CircularProgress,
  Alert,
  Autocomplete,
  FormControlLabel,
  Checkbox,
  Grid,
  Avatar,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  VideocamOutlined as VideocamIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  HelpOutline as MaybeIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { fetchUsers } from '../../../shared/utils/userApi';
import { axiosService } from 'shared/helpers/axios';

// Using native HTML5 datetime-local input for better compatibility
function MeetingScheduleDialog({ open, onClose, call, meetingRequestId = null, restrictToUsers = null, onDone }) {
  // Format date for datetime-local input (YYYY-MM-DDTHH:MM)
  const formatDateTimeLocal = (date) => {
    const pad = (num) => String(num).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  };

  const now = new Date();
  const oneHourLater = new Date(now.getTime() + 60 * 60 * 1000);

  // State declarations
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [invitees, setInvitees] = useState([]);
  const [, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [pollData, setPollData] = useState(null);
  const [timeSlots, setTimeSlots] = useState([
    { start: formatDateTimeLocal(now), end: formatDateTimeLocal(oneHourLater) },
  ]);
  const [createPoll, setCreatePoll] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Initialize invitees from restrictToUsers when dialog opens
  useEffect(() => {
    if (open) {
      // Reset all form fields when dialog opens
      if (!meetingRequestId) {
        setTitle('');
        setDescription('');
        setCreatePoll(true);
        setTimeSlots([{ start: formatDateTimeLocal(now), end: formatDateTimeLocal(oneHourLater) }]);
        setError('');
        setSuccess(false);
        setPollData(null);
      }

      // Set invitees from restrictToUsers if provided
      if (restrictToUsers && Array.isArray(restrictToUsers)) {
        const validUsers = restrictToUsers.filter(user =>
          user && (user.id || user.pk || user.public_id)
        );
        setInvitees(validUsers);
      } else {
        setInvitees([]);
      }

      // Clear search results
      setSearchResults([]);
      setSearchTerm('');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, restrictToUsers, meetingRequestId]);

  // Fetch meeting request data when editing
  const fetchMeetingData = useCallback(async () => {
    if (!open || !meetingRequestId) return;

    setLoadingData(true);
    try {
      const meetingRequestResponse = await axiosService.get(
        `/meeting/meeting-requests/${meetingRequestId}/`
      );
      const meetingRequest = meetingRequestResponse.data;

      setTitle(meetingRequest.title || '');
      setDescription(meetingRequest.description || '');

      // Fetch invitees details
      let inviteesData = [];
      if (meetingRequest.invitees && meetingRequest.invitees.length > 0) {
        inviteesData = await Promise.all(
          meetingRequest.invitees.map(async (inviteeId) => {
            try {
              const userResponse = await axiosService.get(`/users/${inviteeId}/`);
              return userResponse.data;
            } catch (err) {
              console.error(`Failed to fetch user ${inviteeId}:`, err);
              return null;
            }
          })
        );
        inviteesData = inviteesData.filter(Boolean);
      }

      // Combine restrictToUsers with fetched invitees
      let finalInvitees = [];
      if (restrictToUsers && Array.isArray(restrictToUsers)) {
        const restrictIds = new Set(restrictToUsers.map(u => String(u.id || u.pk || u.public_id)));
        const additionalInvitees = inviteesData.filter(user =>
          !restrictIds.has(String(user.id || user.pk || user.public_id))
        );
        finalInvitees = [...restrictToUsers, ...additionalInvitees];
      } else {
        finalInvitees = inviteesData;
      }
      setInvitees(finalInvitees);

      // Try to fetch associated poll if it exists
      try {
        const pollsResponse = await axiosService.get('/meeting/meeting-polls/', {
          params: { meeting_request: meetingRequestId }
        });

        if (pollsResponse.data && pollsResponse.data.length > 0) {
          const poll = pollsResponse.data[0];
          setPollData(poll);

          if (poll.options && poll.options.length > 0) {
            const formattedSlots = poll.options.map((option) => ({
              id: option.id,
              start: formatDateTimeLocal(new Date(option.start)),
              end: formatDateTimeLocal(new Date(option.end)),
              votes: {
                yes: option.yes || 0,
                ifneedbe: option.ifneedbe || 0,
                no: option.no || 0,
              }
            }));
            setTimeSlots(formattedSlots);
            setCreatePoll(false);
          }
        }
      } catch (_) {
        // Poll might not exist yet, which is fine
      }
    } catch (err) {
      console.error('Error fetching meeting data:', err);
      setError('Failed to load meeting data');
    } finally {
      setLoadingData(false);
    }
  }, [open, meetingRequestId, restrictToUsers]);

  // Load meeting data when dialog opens for editing
  useEffect(() => {
    if (open && meetingRequestId) {
      fetchMeetingData();
    }
  }, [open, meetingRequestId, fetchMeetingData]);

  // Search for users with restrictions
  const loadInitialUsers = useCallback(async () => {
    setLoadingSearch(true);
    try {
      const res = await axiosService.get('/users/', { params: { page_size: 50 } });
      let results = res.data?.results ?? res.data ?? [];
      if (restrictToUsers && Array.isArray(restrictToUsers)) {
        const restrictedIds = new Set(restrictToUsers.map(u => String(u.id || u.pk || u.public_id)));
        results = results.filter(user => restrictedIds.has(String(user?.id || user?.pk || user?.public_id)));
      }
      setSearchResults(results);
    } catch (err) {
      console.error('Error loading initial users:', err);
    } finally {
      setLoadingSearch(false);
    }
  }, [restrictToUsers]);

  // Pre-load users when dialog opens so dropdown is populated on first click
  useEffect(() => {
    if (open) loadInitialUsers();
  }, [open, loadInitialUsers]);

  const handleSearchUsers = useCallback(async (searchQuery) => {
    if (!searchQuery || searchQuery.trim().length < 2) {
      loadInitialUsers();
      return;
    }

    setLoadingSearch(true);
    try {
      const results = await fetchUsers(searchQuery);

      if (restrictToUsers && Array.isArray(restrictToUsers)) {
        const restrictedIds = new Set(restrictToUsers.map(u => String(u.id || u.pk || u.public_id)));
        const filtered = results.filter(user =>
          restrictedIds.has(String(user?.id || user?.pk || user?.public_id))
        );
        setSearchResults(filtered);
      } else {
        setSearchResults(results);
      }
    } catch (err) {
      console.error('Error searching users:', err);
      setSearchResults([]);
    } finally {
      setLoadingSearch(false);
    }
  }, [restrictToUsers, loadInitialUsers]);

  // Handle user selection in autocomplete
  const handleInviteesChange = (_, newValue) => {
    if (restrictToUsers && Array.isArray(restrictToUsers)) {
      const restrictIds = new Set(restrictToUsers.map(u => String(u.id || u.pk || u.public_id)));
      const restrictedUsers = restrictToUsers.filter(u => restrictIds.has(String(u.id || u.pk || u.public_id)));

      const allInvitees = [
        ...restrictedUsers,
        ...newValue.filter(u => !restrictIds.has(String(u.id || u.pk || u.public_id)))
      ];

      const uniqueInvitees = [];
      const seenIds = new Set();
      allInvitees.forEach(user => {
        const userId = String(user.id || user.pk || user.public_id);
        if (!seenIds.has(userId)) {
          seenIds.add(userId);
          uniqueInvitees.push(user);
        }
      });

      setInvitees(uniqueInvitees);
    } else {
      setInvitees(newValue);
    }
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!title || invitees.length === 0) {
      setError('Please provide a title and at least one invitee');
      return;
    }

    if (createPoll) {
      for (const slot of timeSlots) {
        const start = new Date(slot.start);
        const end = new Date(slot.end);
        if (start >= end) {
          setError('End time must be after start time for all time slots');
          return;
        }
      }
    }

    setSubmitting(true);
    setError('');

    try {
      const requestData = {
        call_id: call?.id,
        title: title || `Meeting for ${call?.title || 'Call'}`,
        description: description || '',
        invitee_ids: invitees.map((inv) => inv.id || inv.pk || inv.public_id),
      };

      let meetingRequest;

      if (meetingRequestId) {
        const requestResponse = await axiosService.patch(
          `/meeting/meeting-requests/${meetingRequestId}/`,
          requestData
        );
        meetingRequest = requestResponse.data;
      } else {
        const requestResponse = await axiosService.post(
          '/meeting/meeting-requests/',
          requestData
        );
        meetingRequest = requestResponse.data;
      }

      if (createPoll && timeSlots.length > 0) {
        const pollPayload = {
          meeting_request_id: meetingRequest.id,
          title: `Time Poll: ${title}`,
          description: 'Please vote for your availability',
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          options: timeSlots.map((slot) => ({
            start: new Date(slot.start).toISOString(),
            end: new Date(slot.end).toISOString(),
          })),
        };

        if (pollData?.id) {
          await axiosService.patch(`/meeting/meeting-polls/${pollData.id}/`, pollPayload);
        } else {
          await axiosService.post('/meeting/meeting-polls/', pollPayload);
        }
      } else if (pollData?.id && !createPoll) {
        await axiosService.delete(`/meeting/meeting-polls/${pollData.id}/`);
      }

      setSuccess(true);
      onDone?.();
      setTimeout(() => {
        handleClose();
      }, 2000);

    } catch (err) {
      console.error('Error creating/updating meeting:', err);
      setError(err.response?.data?.detail || 'Failed to schedule meeting. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = (e) => {
    setTitle('');
    setDescription('');
    setInvitees([]);
    setSearchTerm('');
    setSearchResults([]);
    setTimeSlots([{
      start: formatDateTimeLocal(now),
      end: formatDateTimeLocal(oneHourLater),
    }]);
    setCreatePoll(true);
    setError('');
    setSuccess(false);
    setPollData(null);
    setSubmitting(false);
    onClose(e);
  };

  const addTimeSlot = () => {
    const lastSlot = timeSlots[timeSlots.length - 1];
    let newStartDate;
    if (lastSlot) {
      const lastDate = new Date(lastSlot.start);
      newStartDate = new Date(lastDate.getTime() + 24 * 60 * 60 * 1000);
    } else {
      newStartDate = new Date();
    }
    const newEndDate = new Date(newStartDate.getTime() + 60 * 60 * 1000);
    setTimeSlots([...timeSlots, {
      start: formatDateTimeLocal(newStartDate),
      end: formatDateTimeLocal(newEndDate)
    }]);
  };

  const removeTimeSlot = (index) => {
    setTimeSlots(timeSlots.filter((_, i) => i !== index));
  };

  const updateTimeSlot = (index, field, value) => {
    const updated = [...timeSlots];
    updated[index][field] = value;
    setTimeSlots(updated);
  };

  const getUserDisplayName = (user) => {
    if (typeof user === 'string') return user;
    if (user?.name) return user.name;
    if (user?.first_name && user?.last_name) return `${user.first_name} ${user.last_name}`.trim();
    if (user?.first_name) return user.first_name;
    if (user?.last_name) return user.last_name;
    if (user?.username) return user.username;
    if (user?.email) return user.email;
    return '';
  };

  const isRestrictedUser = (user) => {
    if (!restrictToUsers || !Array.isArray(restrictToUsers)) return false;
    const userId = String(user.id || user.pk || user.public_id);
    return restrictToUsers.some(u => String(u.id || u.pk || u.public_id) === userId);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      onClick={(e) => e.stopPropagation()}
      sx={{ zIndex: 3000 }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center">
            <VideocamIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">
              {meetingRequestId ? 'Meeting Details' : 'Schedule a Meeting'}
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {loadingData ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
            <CircularProgress />
          </Box>
        ) : success ? (
          <Alert severity="success" sx={{ mb: 2 }}>
            {meetingRequestId ? 'Meeting updated successfully!' : 'Meeting request sent successfully! Notifications have been sent to all invitees.'}
          </Alert>
        ) : (
          <Box>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                {error}
              </Alert>
            )}

            {call && (
              <Alert severity="info" sx={{ mb: 2 }}>
                This meeting is for: <strong>{call.title}</strong>
              </Alert>
            )}

            {restrictToUsers && Array.isArray(restrictToUsers) && restrictToUsers.length > 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                This meeting is restricted to {restrictToUsers.length} specific user(s)
              </Alert>
            )}

            <TextField
              label="Meeting Title *"
              fullWidth
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={`Meeting for ${call?.title || 'Call'}`}
              sx={{ mb: 2 }}
              required
            />

            <TextField
              label="Description (optional)"
              fullWidth
              multiline
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Add meeting agenda or notes..."
              sx={{ mb: 3 }}
            />

            <Typography variant="subtitle2" gutterBottom>
              Invite Colleagues *
            </Typography>
            <Autocomplete
              multiple
              freeSolo
              options={searchResults}
              value={invitees}
              onChange={handleInviteesChange}
              onInputChange={(_, newInputValue) => {
                setSearchTerm(newInputValue);
                handleSearchUsers(newInputValue);
              }}
              getOptionLabel={(option) => getUserDisplayName(option)}
              isOptionEqualToValue={(opt, val) =>
                String(opt?.id ?? opt?.pk ?? opt?.public_id ?? '') ===
                String(val?.id ?? val?.pk ?? val?.public_id ?? '')
              }
              loading={loadingSearch}
              disabled={!!meetingRequestId && !restrictToUsers}
              filterOptions={(x) => x}
              slotProps={{ popper: { style: { zIndex: 3001 } } }}
              renderOption={(props, option) => (
                <li {...props}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar
                      src={option.avatar || option.avatar_url}
                      sx={{ width: 24, height: 24, bgcolor: 'rgba(118, 75, 162, 0.85)', fontSize: '0.75rem' }}
                    >
                      {getUserDisplayName(option)[0]?.toUpperCase()}
                    </Avatar>
                    <Typography variant="body2">
                      {getUserDisplayName(option)}
                      {option?.email && (
                        <Typography component="span" variant="caption" color="textSecondary" sx={{ ml: 0.5 }}>
                          ({option.email})
                        </Typography>
                      )}
                    </Typography>
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={restrictToUsers ? 'Search restricted users...' : 'Search by name or email...'}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingSearch ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    avatar={
                      <Avatar src={option.avatar || option.avatar_url}
                        sx={{ bgcolor: 'rgba(34, 111, 153, 0.85)', color: '#fff !important', fontWeight: 'bold', width: 24, height: 24 }}>
                        {getUserDisplayName(option)[0]?.toUpperCase()}
                      </Avatar>
                    }
                    label={getUserDisplayName(option)}
                    {...getTagProps({ index })}
                    key={index}
                    color="default"
                    variant={isRestrictedUser(option) ? 'filled' : 'outlined'}
                    onDelete={isRestrictedUser(option) ? undefined : getTagProps({ index }).onDelete}
                  />
                ))
              }
              sx={{ mb: 3 }}
            />

            {invitees.length > 0 && (
              <Box sx={{ mb: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle2" gutterBottom>
                  Invitees ({invitees.length})
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {invitees.map((invitee, idx) => (
                    <Chip
                      key={idx}
                      avatar={
                        <Avatar src={invitee.avatar || invitee.avatar_url}
                          sx={{ width: 24, height: 24, bgcolor: 'rgba(34, 111, 153, 0.85)', color: '#fff !important', fontWeight: 'bold' }}>
                          {getUserDisplayName(invitee)[0]?.toUpperCase()}
                        </Avatar>
                      }
                      label={getUserDisplayName(invitee)}
                      size="small"
                      color="default"
                      variant={isRestrictedUser(invitee) ? 'filled' : 'outlined'}
                    />
                  ))}
                </Stack>
              </Box>
            )}

            <FormControlLabel
              control={
                <Checkbox
                  checked={createPoll}
                  onChange={(e) => setCreatePoll(e.target.checked)}
                  disabled={!!pollData && !createPoll}
                />
              }
              label="Create poll for scheduling time"
            />

            {createPoll && (
              <Box sx={{ mt: 2 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="subtitle2">Proposed Time Slots</Typography>
                  {!pollData && (
                    <Button size="small" startIcon={<AddIcon />} onClick={addTimeSlot}>
                      Add Slot
                    </Button>
                  )}
                </Box>

                <Stack spacing={2}>
                  {timeSlots.map((slot, index) => (
                    <Box
                      key={index}
                      sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'background.paper' }}
                    >
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={5}>
                          <TextField
                            label="Start Time *"
                            type="datetime-local"
                            value={slot.start}
                            onChange={(e) => updateTimeSlot(index, 'start', e.target.value)}
                            fullWidth
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            disabled={!!pollData}
                            required
                          />
                        </Grid>
                        <Grid item xs={5}>
                          <TextField
                            label="End Time *"
                            type="datetime-local"
                            value={slot.end}
                            onChange={(e) => updateTimeSlot(index, 'end', e.target.value)}
                            fullWidth
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            disabled={!!pollData}
                            required
                          />
                        </Grid>
                        <Grid item xs={2}>
                          {timeSlots.length > 1 && !pollData && (
                            <IconButton onClick={() => removeTimeSlot(index)} color="error" size="small">
                              <DeleteIcon />
                            </IconButton>
                          )}
                        </Grid>
                      </Grid>

                      {slot.votes && Object.keys(slot.votes).length > 0 && (
                        <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                          <Typography variant="caption" color="text.secondary" gutterBottom>
                            Responses:
                          </Typography>
                          <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                            {slot.votes.yes > 0 && (
                              <Chip icon={<CheckIcon />} label={`${slot.votes.yes} Yes`} size="small" color="success" variant="outlined" />
                            )}
                            {slot.votes.ifneedbe > 0 && (
                              <Chip icon={<MaybeIcon />} label={`${slot.votes.ifneedbe} Maybe`} size="small" color="warning" variant="outlined" />
                            )}
                            {slot.votes.no > 0 && (
                              <Chip icon={<CancelIcon />} label={`${slot.votes.no} No`} size="small" color="error" variant="outlined" />
                            )}
                          </Stack>
                        </Box>
                      )}
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={submitting} sx={{ color: 'text.secondary' }}>
          {meetingRequestId ? 'Close' : 'Cancel'}
        </Button>
        {(!meetingRequestId || !pollData) && (
          <Button
            sx={{ bgcolor: 'rgba(34, 111, 153, 0.85)', color: '#fff !important' }}
            onClick={handleSubmit}
            variant="contained"
            disabled={submitting || success}
            startIcon={submitting ? <CircularProgress size={16} /> : <VideocamIcon />}
          >
            {submitting
              ? (meetingRequestId ? 'Updating...' : 'Sending...')
              : (meetingRequestId ? 'Update Meeting' : 'Send Meeting Request')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default MeetingScheduleDialog;
